import * as React from "react"

import { FormFieldShell, type FormFieldProps } from "@/components/forms/FormField"
import { Textarea } from "@/components/ui/textarea"

export type FormTextareaProps = FormFieldProps &
  Omit<React.ComponentProps<typeof Textarea>, "className"> & {
    /** Class names applied to the textarea control (not the field wrapper). */
    inputClassName?: string
  }

/**
 * Labeled textarea with description / error associations and required marker.
 *
 * Same field API as {@link FormInput}; pass textarea props or `register` through.
 *
 * @example RHF
 * ```tsx
 * <FormTextarea
 *   label="Notes"
 *   description="Optional context for the booking team."
 *   error={errors.notes?.message}
 *   {...register("notes")}
 * />
 * ```
 *
 * @example Controlled (no RHF)
 * ```tsx
 * <FormTextarea
 *   label="Notes"
 *   value={notes}
 *   onChange={(e) => setNotes(e.target.value)}
 * />
 * ```
 */
export function FormTextarea({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  id,
  ref,
  disabled,
  "aria-invalid": ariaInvalid,
  ...textareaProps
}: FormTextareaProps) {
  const generatedId = React.useId()
  const textareaId = id ?? generatedId
  const invalid =
    Boolean(error) ||
    (ariaInvalid != null && ariaInvalid !== false && ariaInvalid !== "false")

  return (
    <FormFieldShell
      label={label}
      required={required}
      description={description}
      error={error}
      fieldClassName={fieldClassName}
      htmlFor={textareaId}
      invalid={invalid}
      disabled={disabled}
    >
      {(controlProps) => (
        <Textarea
          {...textareaProps}
          {...controlProps}
          ref={ref}
          required={required}
          disabled={disabled}
          aria-invalid={ariaInvalid ?? (error ? true : undefined)}
          aria-describedby={
            [
              controlProps["aria-describedby"],
              textareaProps["aria-describedby"],
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
          className={inputClassName}
        />
      )}
    </FormFieldShell>
  )
}
